import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Please share your name, email and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent. We'll reply within one business day.");
      setForm({ full_name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Message failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer id="contact" className="bg-deepink text-cream relative grain overflow-hidden" data-testid="site-footer">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-12">
        {/* Huge CTA */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1 }}
          className="border-b border-cream/15 pb-16 md:pb-24 mb-16"
        >
          <div className="text-gold text-[11px] tracking-[0.3em] mb-8">CHAPTER 10 · LET'S TALK</div>
          <h2 className="font-serif text-6xl sm:text-7xl md:text-8xl lg:text-[9rem] font-light leading-[0.9] tracking-tight">
            Write to us. <br />
            <span className="italic text-gold">We'll write back.</span>
          </h2>
        </motion.div>

        {/* Two-col: contact info + form */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 mb-24">
          <div className="lg:col-span-5 space-y-10">
          <div>
            <div className="text-[10px] tracking-[0.3em] text-cream/50 mb-3">CALL</div>
            <a href="tel:+13468393181" className="font-serif text-2xl link-line" data-testid="footer-phone">
              (346) 839 · 3181
            </a>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] text-cream/50 mb-3">WRITE</div>
            <a href="mailto:Dylanwebb778@gmail.com" className="font-serif text-2xl link-line" data-testid="footer-email">
              Dylanwebb778@gmail.com
            </a>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] text-cream/50 mb-3">HOURS</div>
            <div className="text-cream/80 leading-relaxed">
              Monday – Sunday · 8am – 9pm
            </div>
          </div>
          </div>

          <div className="lg:col-span-7">
            {sent ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="border border-cream/20 p-12 text-center"
                data-testid="contact-success"
              >
                <div className="font-serif italic text-gold text-4xl mb-4">Thank you.</div>
                <p className="text-cream/70">Your message is with us. We'll reply within one business day.</p>
                <button
                  onClick={() => setSent(false)}
                  className="mt-8 text-[11px] tracking-[0.3em] text-cream/60 hover:text-gold"
                  data-testid="contact-reset-btn"
                >
                  SEND ANOTHER →
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2" data-testid="contact-form">
                <div>
                  <label className="text-[10px] tracking-[0.3em] text-cream/50">FULL NAME</label>
                  <input className="line-input-dark" value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    data-testid="contact-name" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.3em] text-cream/50">EMAIL</label>
                  <input className="line-input-dark" type="email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="contact-email" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] tracking-[0.3em] text-cream/50">PHONE (OPTIONAL)</label>
                  <input className="line-input-dark" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="contact-phone" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] tracking-[0.3em] text-cream/50">MESSAGE</label>
                  <textarea className="line-input-dark resize-none" rows={4} value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="contact-message" />
                </div>
                <div className="md:col-span-2 mt-8">
                  <button
                    type="submit"
                    disabled={sending}
                    className="py-5 px-10 bg-cream text-ink text-[12px] tracking-[0.3em] uppercase hover:bg-gold transition-colors duration-500 disabled:opacity-60"
                    data-testid="contact-submit-btn"
                  >
                    {sending ? "Sending…" : "Send message →"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        <div className="border-t border-cream/15 pt-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 text-[11px] tracking-[0.2em] text-cream/50">
          <div>© {new Date().getFullYear()} CALLAHAN &amp; CO. · LICENSED INSURANCE BROKERAGE · TX #4210942</div>
          <div className="flex gap-8">
            <a href="#" className="link-line" data-testid="footer-privacy">PRIVACY</a>
            <a href="#" className="link-line" data-testid="footer-terms">TERMS</a>
            <a href="#" className="link-line" data-testid="footer-licenses">LICENSES</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
